import {CLASS_NAMES} from '@github-ui/commenting/DomElements'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useCurrentUser} from '@github-ui/current-user'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {KeyIcon, LockIcon} from '@primer/octicons-react'
import {Box, Button, CounterLabel, Flash} from '@primer/react'
import clsx from 'clsx'
import {useEffect, useMemo, useState} from 'react'

import {useDiscussionComments} from '../../../hooks/use-discussion-comments'
import type {CommitComment, InitialCommentInfo} from '../../../types/commit-types'
import type {Commit} from '../../../types/shared'
import {isWeirichCommit} from '../../../utils/weirich-commit'
import styles from './Comment.module.css'
import {CommentLoading} from './CommentLoading'
import {ExistingCommitComments} from './ExistingCommitComments'
import {LockConversationDialog} from './LockConversationDialog'
import {NewCommitComment} from './NewCommitComment'
import {NotificationsFooter} from './NotificationFooter'

// id to jump to when navigating to the comments section from commits list
export const COMMENTS_CONTAINER_ID = 'comments'

export function DiscussionComments({commit, commentInfo}: {commit: Commit; commentInfo: InitialCommentInfo}) {
  const repo = useCurrentRepository()
  const currentUser = useCurrentUser()
  const {
    comments,
    setComments,
    commentCount,
    setCommentCount,
    hasMoreComments,
    subscribed,
    loadDiscussionComments,
    loaded,
    didError,
  } = useDiscussionComments(commit, repo)

  // Used for quote replies
  const [newCommentContent, setNewCommentContent] = useState<string | undefined>(undefined)
  const [locked, setLocked] = useState(commentInfo.locked)

  const isWeirichCommitValue = useMemo(() => isWeirichCommit(commit.oid, repo.id), [commit.oid, repo.id])

  const addComment = (comment: CommitComment) => {
    setComments([...comments, comment])
    setCommentCount((commentCount ?? 0) + 1)
  }

  const deleteComment = (comment: CommitComment) => {
    setComments(comments.filter(c => c.id !== comment.id))
    setCommentCount((commentCount ?? 0) - 1)
  }

  useEffect(() => {
    if (ssrSafeWindow?.location.hash) {
      const urlFragment = ssrSafeWindow.location.hash.slice(1)
      const comment = comments.find(c => c.urlFragment === urlFragment)

      if (comment) {
        const commentElement = document.getElementById(urlFragment)
        if (commentElement) {
          commentElement.scrollIntoView()
          commentElement.focus()
        }
      }
    }
  }, [comments])

  return (
    <Box
      className={clsx([
        'd-flex flex-column gap-2 pt-3',
        CLASS_NAMES.commentsContainer,
        styles['commit-discussion-comments'],
      ])}
    >
      <DiscussionCommentsHeader
        commitOid={commit.oid}
        commentCount={commentCount}
        canLock={commentInfo.canLock}
        locked={locked}
        setLocked={setLocked}
      />
      {!loaded ? (
        didError ? (
          <Flash className="d-flex flex-justify-between flex-items-center" variant="danger">
            <span>Failed to load comments.</span>
            <Button onClick={() => loadDiscussionComments()}>Retry</Button>
          </Flash>
        ) : (
          <CommentLoading />
        )
      ) : hasMoreComments ? (
        <Button className="width-full" onClick={() => loadDiscussionComments(comments[0]?.id.toString())}>
          Load more comments
        </Button>
      ) : null}
      <ExistingCommitComments
        comments={comments}
        commit={commit}
        locked={locked}
        deleteComment={deleteComment}
        setComments={setComments}
        setNewCommentContent={setNewCommentContent}
      />
      {isWeirichCommitValue ? (
        <div className="text-center">
          {[...Array(38)].map((_, i) => (
            <img
              key={`rose-${i}`}
              alt="rose"
              src="/images/icons/emoji/rose.png"
              className={styles['discussion-comments-rose']}
            />
          ))}
        </div>
      ) : null}
      {loaded || comments.length !== 0 ? (
        <>
          <NewCommitComment
            commitOid={commit.oid}
            onAddComment={addComment}
            newCommentContent={newCommentContent}
            canComment={commentInfo.canComment}
            locked={locked}
            repoArchived={commentInfo.repoArchived}
          />
          {currentUser ? <NotificationsFooter commitOid={commit.oid} subscribed={subscribed ?? false} /> : null}
        </>
      ) : null}
    </Box>
  )
}

function DiscussionCommentsHeader({
  commitOid,
  commentCount,
  locked,
  setLocked,
  canLock,
}: {
  commitOid: string
  commentCount: number | undefined
  locked: boolean
  setLocked: (locked: boolean) => void
  canLock: boolean
}) {
  const [lockDialogOpen, setLockDialogOpen] = useState(false)
  return (
    <div className="d-flex flex-items-center flex-justify-between" id={COMMENTS_CONTAINER_ID}>
      <h2 className="sr-only">{commentCount} commit comments</h2>
      <div className="d-flex flex-items-center">
        <div className="h4 pr-2">Comments</div>
        {commentCount !== undefined && <CounterLabel>{commentCount}</CounterLabel>}
      </div>
      {canLock && (
        <>
          <Button
            leadingVisual={locked ? KeyIcon : LockIcon}
            variant="invisible"
            onClick={() => setLockDialogOpen(true)}
          >
            {locked ? 'Unlock' : 'Lock'} conversation
          </Button>
          {lockDialogOpen && (
            <LockConversationDialog
              commitOid={commitOid}
              locked={locked}
              onClose={newLockedState => {
                setLockDialogOpen(false)
                if (newLockedState !== undefined) {
                  setLocked(newLockedState)
                }
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

try{ DiscussionComments.displayName ||= 'DiscussionComments' } catch {}
try{ DiscussionCommentsHeader.displayName ||= 'DiscussionCommentsHeader' } catch {}